import { languageSchema } from "./languageSchema.js";
import { makeI18nField } from "../utils/i18nSchemas.js";

export const sessionSchema = {
  type: "object",
  properties: {
    title: { type: "string", minLength: 1 },
    titleI18n: makeI18nField({ required: true, minLength: 1 }),
    description: { type: "string" },
    descriptionI18n: makeI18nField(),
    descriptionRichI18n: true, // Skip validation to preserve whitespace
    locationId: { type: ["number", "null"] },
    starting: { type: "string", format: "iso-date-time" },
    ending: { type: "string", format: "iso-date-time" },
    sponsorId: { type: ["number", "null"] },
    eventId: { type: "number" },
    speakers: { type: ["array", "null"], items: { type: "number" } },
    tags: { type: "array", items: { type: ["number", "null"] } },
    targetedTagIds: { type: ["array", "null"], items: { type: "number" } },
    isClickable: { type: "boolean" },
  },
  additionalProperties: false,
  required: ["titleI18n", "eventId", "locationId", "starting", "ending"],
};

// Schema for creating session, without title and description
export const createSessionSchema = {
  ...sessionSchema,
  // Remove title and description from the schema
  properties: Object.fromEntries(
    Object.entries(sessionSchema.properties).filter(
      ([key]) => key !== "title" && key !== "description"
    )
  ),
  required: sessionSchema.required,
};

// Schema for updating session
export const updateSessionSchema = {
  ...sessionSchema,
  minProperties: 1,
  properties: Object.fromEntries(
    Object.entries(sessionSchema.properties).filter(
      ([key]) => key !== "title" && key !== "description"
    )
  ),
  required: [],
};

export const getSessionsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "string" },
      dateId: { type: "string" },
      ...languageSchema.properties,
      page: { type: "number" },
      locationFilter: { type: "string" },
      speakerFilter: { type: "string" },
      searchParams: { type: "string" },
      sort: { type: "string" },
    },
    required: ["eventId"],
  },
};

export const getPublicSessionsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      locationId: { type: "string" },
      dateId: { type: "string" },
      ...languageSchema.properties,
    },
    required: ["eventId"],
  },
};

export const getSessionByIdSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: { id: { type: "string" } },
    required: ["id"],
  },
};

export const deleteSessionSchema = {
  body: {
    type: "object",
    properties: { id: { type: "number" } },
    required: ["id"],
  },
};

export const createSessionLinkSchema = {
  body: {
    type: "object",
    properties: {
      url: { type: ["string", "null"] },
      fileKey: { type: ["string", "null"] },
      title: { type: "string" },
    },
    required: ["title"],
  },
};

export const deleteSessionLinkSchema = {
  body: {
    type: "object",
    properties: { id: { type: "number" } },
    required: ["id"],
  },
};

export const patchSessionLinkSchema = {
  body: {
    type: "object",
    properties: { title: { type: "string" } },
    required: ["title"],
  },
};

export const web_sessionLinkSchema = {
  type: "object",
  properties: {
    title: { type: "string", minLength: 1 },
    url: { type: ["string", "null"], pattern: "^(https?://.*)?$" },
    fileKey: { type: ["string", "null"] },
  },
  required: ["title"],
};

export const web_sessionFormSchema = {
  type: "object",
  properties: {
    titleI18n: makeI18nField({ required: true, minLength: 1 }),
    descriptionI18n: makeI18nField(),
    descriptionRichI18n: makeI18nField({ type: ["object", "null"] }),
    locationId: { type: "string", minLength: 1 },
    starting: {
      type: ["string", "object"],
      format: "iso-date-time",
      errorMessage: "Starting date is required",
    },
    ending: {
      type: ["string", "object"],
      format: "iso-date-time",
      errorMessage: "Ending date is required",
    },
    tags: {
      type: "object",
      properties: {
        dressCode: { type: "array" },
        sessionType: { type: "array" },
        topic: { type: "array" },
      },
    },
    sponsorId: { type: "string", nullable: true },
    speakers: { type: "array", items: { type: "string" } },
    targetedTagIds: { type: "array", items: { type: "string" } },
    isClickable: { type: "boolean" },
  },
  required: ["titleI18n", "locationId", "starting", "ending"],
};

export const getSessionRating = {
  params: {
    type: "object",
    properties: {
      id: { type: "string" },
    },
    required: ["id"],
  },
};

export const addSessionRating = {
  params: {
    type: "object",
    properties: {
      id: { type: "string" },
    },
    required: ["id"],
  },
  body: {
    type: "object",
    properties: {
      rating: { type: "integer", minimum: 1, maximum: 5 },
    },
    required: ["rating"],
  },
};
